import { defineStore } from "pinia";
import { api } from "~/api";
import { AdvancedChannel, SubscriptionListResponse } from "~/api/messages/channel/subscription-list.response";
import { ChannelSubscriptionState, ChannelTransport } from "~/types/channel-subscription.type";
import { useTemplates } from "./template.store";

type Channels = Array<AdvancedChannel>;

// Типы
interface State {
  channels: Channels;
  selectedWabaChannel: SubscriptionListResponse | null;
  selectedTelegramChannel: SubscriptionListResponse | null;
  isFetching: boolean;
  telegramError: string;
}

// Утилиты
const findChannelBy = (
  channels: Channels,
  key: keyof SubscriptionListResponse,
  value: string,
) => {
  return channels.find((channel) => channel[key] === value) || null;
};

const filterChannelsByTransport = (
  channels: Channels,
  transport: ChannelTransport,
  state?: ChannelSubscriptionState,
) => {
  return channels.filter(
    (channel) =>
      channel.transport === transport && (!state || channel.state === state),
  );
};

export const useChannels = defineStore("channels", {
  state: (): State => ({
    channels: [],
    isFetching: false,
    telegramError: "",
    selectedWabaChannel: null,
    selectedTelegramChannel: null,
  }),

  actions: {
    async fetchChannels() {
      const channels = await api.channels.getListOfChannels();
      if (!channels.length) return channels

      const arrSubscriptionIds = channels.map(({ subscriptionId }) => subscriptionId);
      const sessions = await api.sessions.getActivateSessions(arrSubscriptionIds).catch(() => undefined);
      this.channels = channels.map((c) => {
        const activeSessions = sessions ? sessions[c.subscriptionId as keyof typeof sessions] : '-';
        return {
          ...c,
          activeSessions
        }
      }) as AdvancedChannel[];
    },
    async activateChannels() {
      if (!this.channels.length) return;
      const subscriptionIds = this.channels.map(
        ({ subscriptionId }) => subscriptionId,
      );
      await api.router.activateSubscription(subscriptionIds);
    },
    async setSelectedWabaChannel(
      channelId: string | null,
    ): Promise<SubscriptionListResponse | null> {
      const templateStore = useTemplates();
      const channel = this.$state.channels.find((c) => c.id == channelId);
      if (!channel) {
        return null;
      }
      this.selectedWabaChannel = channel;

      return new Promise((r) => {
        templateStore.fetchChannelTemplates().then(() => {
          r(this.selectedWabaChannel);
        });
      });
    },
    syncChannelsByWazzupUserKey(key: string) {
      api.channels.syncWazzupChannelsByAccountKey(key).then((res) => {
        if (res) {
          const subscriptionIds = res.data.map(({ subscriptionId }) => subscriptionId)
          api.router.activateSubscription(subscriptionIds);
        }
        this.fetchChannels();
      });
    },
    setTelegramError(payload: string) {
      this.telegramError = payload;
    },
    setFetching(payload: boolean) {
      this.isFetching = payload;
    },
    async setSelectedWabaChannelBySubscriptionId(
      subscriptionId: string,
    ): Promise<SubscriptionListResponse | null> {
      const channel = findChannelBy(
        this.channels,
        "subscriptionId",
        subscriptionId,
      );
      if (!channel) {
        return null;
      }
      return this.setSelectedWabaChannel(channel!.id);
    },
  },

  getters: {
    getChannels: (state: State) => state.channels,
    getFetching: (state: State) => state.isFetching,
    getNonClosedChannels: (state) => {
      const nonClosedStates = [
        ChannelSubscriptionState.blocked,
        ChannelSubscriptionState.rejected,
        ChannelSubscriptionState.disabled,
      ];
      return state.channels.filter((channel) =>
        nonClosedStates.includes(channel.state),
      );
    },
    wabaChannels: (state) =>
      filterChannelsByTransport(state.channels, ChannelTransport.wapi),
    telegramChannels: (state) =>
      filterChannelsByTransport(state.channels, ChannelTransport.telegram),
    wabaChannelsActive: (state) =>
      filterChannelsByTransport(
        state.channels,
        ChannelTransport.wapi,
        ChannelSubscriptionState.active,
      ),
    telegramChannelsActive: (state) =>
      filterChannelsByTransport(
        state.channels,
        ChannelTransport.telegram,
        ChannelSubscriptionState.active,
      ),
    selectedWabaChannelId: (state: State): string | null => {
      return state.selectedWabaChannel?.id || null;
    },
  },
});
